import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useOrg } from '../../components/OrgLayout'
import { supabase } from '../../lib/supabase'
import type { OrganizationMember, OrgRole } from '../../types/database'
import { ORG_ROLES } from '../../types/database'
import { useOrgPermissions } from '../../hooks/useOrgPermissions'
import {
  VStack,
  HStack,
  Card,
  Text,
  Button,
  Badge,
  Banner,
  Skeleton,
  Avatar,
  ConfirmDialog,
  Animate
} from 'wss3-forge'
import {
  ArrowLeft20Regular,
  Person20Regular,
  Mail20Regular,
  Calendar20Regular,
  Clock20Regular,
  Shield20Regular,
  Delete20Regular,
  Checkmark20Regular
} from '@fluentui/react-icons'

interface MemberWithEmail extends OrganizationMember {
  email?: string
  avatar_url?: string
  display_name?: string
}

function getRoleBadgeVariant(role: OrgRole): 'primary' | 'success' | 'warning' | 'default' {
  switch (role) {
    case 'owner': return 'primary'
    case 'admin': return 'success'
    case 'sso_admin': return 'warning'
    default: return 'default'
  }
}

export function OrgMemberDetail() {
  const { memberId } = useParams()
  const navigate = useNavigate()
  const { organization } = useOrg()
  const { loadPermissions, hasPermission } = useOrgPermissions()

  const [member, setMember] = useState<MemberWithEmail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [changingRole, setChangingRole] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    if (memberId && organization?.id) {
      loadMember()
      loadPermissions(organization.id)
    }
  }, [memberId, organization?.id])

  const loadMember = async () => {
    if (!memberId || !organization?.id) return

    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('organization_members')
        .select('*')
        .eq('id', memberId)
        .eq('organization_id', organization.id)
        .single()

      if (fetchError) throw fetchError

      if (data) {
        // Get avatar and display name if user exists
        if (data.user_id) {
          const { data: userData } = await supabase.rpc('get_user_email', {
            p_user_id: data.user_id
          })

          const userInfo = Array.isArray(userData) ? userData[0] : userData
          setMember({
            ...data,
            email: userInfo?.email || data.invite_email,
            avatar_url: userInfo?.avatar_url || undefined,
            display_name: userInfo?.display_name || undefined
          })
        } else {
          setMember({
            ...data,
            email: data.invite_email
          })
        }
      }
    } catch (err: any) {
      console.error('Error loading member:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleRoleChange = async (newRole: OrgRole) => {
    if (!member) return

    setChangingRole(true)
    setError(null)

    try {
      const { error } = await supabase.rpc('update_org_member_role', {
        p_organization_id: organization.id,
        p_member_id: member.id,
        p_new_role: newRole
      })

      if (error) throw error

      setMember(prev => prev ? { ...prev, role: newRole } : null)
      setSuccess('Role updated successfully')
    } catch (err: any) {
      setError(err.message)
    } finally {
      setChangingRole(false)
    }
  }

  const handleRemoveMember = async () => {
    if (!member) return

    try {
      const { error } = await supabase
        .from('organization_members')
        .delete()
        .eq('id', member.id)

      if (error) throw error

      navigate(`/org/${organization.slug}/members`)
    } catch (err: any) {
      setError(err.message)
    }
    setShowDeleteConfirm(false)
  }

  const canEditRoles = hasPermission('members.edit_roles')
  const canRemove = hasPermission('members.remove')

  if (loading) {
    return (
      <VStack gap="lg">
        <Skeleton width={200} height={32} />
        <Skeleton width="100%" height={300} />
      </VStack>
    )
  }

  if (!member) {
    return (
      <VStack gap="lg">
        <Banner variant="error" title="Member not found">
          This member does not exist or you don't have access.
        </Banner>
        <Button
          variant="ghost"
          icon={<ArrowLeft20Regular />}
          onClick={() => navigate(`/org/${organization.slug}/members`)}
        >
          Back to Members
        </Button>
      </VStack>
    )
  }

  return (
    <VStack gap="lg">
      <Animate type="fadeIn">
        <Button
          variant="ghost"
          icon={<ArrowLeft20Regular />}
          onClick={() => navigate(`/org/${organization.slug}/members`)}
        >
          Back to Members
        </Button>
      </Animate>

      {error && (
        <Banner variant="error" title="Error" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      {success && (
        <Banner variant="success" title="Success" onDismiss={() => setSuccess(null)}>
          {success}
        </Banner>
      )}

      <Animate type="slideInUp" delay={50}>
        <Card padding="xl">
          <VStack gap="xl">
            {/* Header with avatar and name */}
            <HStack gap="lg" style={{ alignItems: 'center' }}>
              <Avatar
                name={member.display_name || member.email || ''}
                src={member.avatar_url}
                size="lg"
              />
              <VStack gap="xs" style={{ alignItems: 'flex-start' }}>
                <Text size="lg" weight="bold">
                  {member.display_name || member.email}
                </Text>
                {member.display_name && member.email && (
                  <Text size="sm" color="muted">{member.email}</Text>
                )}
                <Badge variant={getRoleBadgeVariant(member.role)}>
                  {ORG_ROLES.find(r => r.id === member.role)?.label || member.role}
                </Badge>
              </VStack>
            </HStack>

            {/* Info grid */}
            <HStack gap="xl" style={{ flexWrap: 'wrap' }}>
              <VStack gap="xs">
                <HStack gap="xs" style={{ alignItems: 'center' }}>
                  <Mail20Regular style={{ color: 'var(--text-muted)', fontSize: '14px' }} />
                  <Text size="sm" color="muted">Email</Text>
                </HStack>
                <Text weight="medium">{member.email || member.invite_email || '-'}</Text>
              </VStack>

              <VStack gap="xs" style={{ alignItems: 'flex-start' }}>
                <HStack gap="xs" style={{ alignItems: 'center' }}>
                  <Person20Regular style={{ color: 'var(--text-muted)', fontSize: '14px' }} />
                  <Text size="sm" color="muted">Source</Text>
                </HStack>
                <Badge size="sm">
                  {member.created_via === 'sso' ? 'SSO Login' : member.created_via === 'invite' ? 'Invitation' : 'Manual'}
                </Badge>
              </VStack>

              <VStack gap="xs">
                <HStack gap="xs" style={{ alignItems: 'center' }}>
                  <Calendar20Regular style={{ color: 'var(--text-muted)', fontSize: '14px' }} />
                  <Text size="sm" color="muted">Joined</Text>
                </HStack>
                <Text weight="medium">
                  {member.invite_accepted_at
                    ? new Date(member.invite_accepted_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })
                    : '-'}
                </Text>
              </VStack>

              <VStack gap="xs">
                <HStack gap="xs" style={{ alignItems: 'center' }}>
                  <Clock20Regular style={{ color: 'var(--text-muted)', fontSize: '14px' }} />
                  <Text size="sm" color="muted">Last SSO Login</Text>
                </HStack>
                <Text weight="medium">
                  {member.last_sso_login_at
                    ? new Date(member.last_sso_login_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'Never'}
                </Text>
              </VStack>
            </HStack>
          </VStack>
        </Card>
      </Animate>

      {/* Role Management */}
      {canEditRoles && (
        <Animate type="slideInUp" delay={100}>
          <Card padding="lg">
            <VStack gap="lg">
              <HStack gap="sm" style={{ alignItems: 'center' }}>
                <Shield20Regular style={{ color: 'var(--brand-primary)' }} />
                <Text weight="semibold">Role & Permissions</Text>
              </HStack>

              <Text size="sm" color="muted">
                Select a role for this member. Different roles have different permissions.
              </Text>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '0.75rem'
              }}>
                {ORG_ROLES.map(role => (
                  <Card
                    key={role.id}
                    padding="md"
                    style={{
                      cursor: changingRole ? 'wait' : 'pointer',
                      border: member.role === role.id
                        ? '2px solid var(--brand-primary)'
                        : '1px solid var(--border-primary)',
                      opacity: changingRole ? 0.7 : 1
                    }}
                    onClick={() => !changingRole && member.role !== role.id && handleRoleChange(role.id)}
                  >
                    <VStack gap="xs" style={{ alignItems: 'flex-start' }}>
                      <HStack gap="sm" style={{ alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Badge variant={getRoleBadgeVariant(role.id)}>
                          {role.label}
                        </Badge>
                        {member.role === role.id && (
                          <Checkmark20Regular style={{ color: 'var(--brand-primary)' }} />
                        )}
                      </HStack>
                      <Text size="sm" color="muted">{role.description}</Text>
                    </VStack>
                  </Card>
                ))}
              </div>
            </VStack>
          </Card>
        </Animate>
      )}

      {/* Danger Zone */}
      {canRemove && (
        <Animate type="slideInUp" delay={150}>
          <Card padding="lg" style={{ borderColor: 'var(--status-error)' }}>
            <VStack gap="md">
              <Text weight="semibold" style={{ color: 'var(--status-error)' }}>
                Danger Zone
              </Text>
              <Text size="sm" color="muted">
                Removing this member will revoke their access to the organization.
              </Text>
              <Button
                variant="danger"
                icon={<Delete20Regular />}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Remove Member
              </Button>
            </VStack>
          </Card>
        </Animate>
      )}

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Remove Member"
        description={`Are you sure you want to remove ${member.email} from the organization? This action cannot be undone.`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleRemoveMember}
      />
    </VStack>
  )
}
